import { IPivotItemProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPDefaultButton,
  IPIconButton,
  IPLink,
  IPModal,
  IPPivot,
  IPPivotItem,
  IPPrimaryButton,
  IPSeparator,
  IPText,
  IPToggle,
  IPTooltipHost,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ActivityTrail from 'components/Activity/ActivityTrail';
import AuditTrail from 'components/Audit/AuditTrail';
import { ChecklistContainer, ChecklistMode } from 'components/Checklist/ChecklistContainer';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import { DialogOk } from 'components/Dialogs/DialogOk';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import TaskLinkList from 'components/Links/LinkLists/TaskLinkList';
import OverlayLoader from 'components/Loading/OverlayLoader';
import { ErrorMessage } from 'components/Notification/ErrorMessage';
import InfoMessage from 'components/Notification/InfoMessage';
import WarningMessage from 'components/Notification/WarningMessage';
import {
  cancelIcon,
  globalStackStylesHeight100,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesBold,
} from 'globalStyles';
import Joi from 'joi';
import Task, { TaskTypes, TaskWorkflowStatus } from 'models/tasks/task';
import { TaskCheckList } from 'models/tasks/taskHelperClasses';
import TaskType, { SystemTaskTypes } from 'models/tasks/taskType';
import { TaskDetailView, TaskDetailViewMode, toTaskDetailViewMode } from 'models/userSetting';
import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetSingleTask } from 'services/Api/taskService';
import { apiGetWebhooksForType } from 'services/Api/webhookService';
import { apiRequest } from 'services/Auth/authConfig';
import { TaskContext } from '../Context/TaskContext';
import TaskTypeForm from '../Forms/TaskTypeForm';
import { ISingleTaskTypeProps } from '../SingleTask';
import { canUpdateTaskField, TaskFieldTypes } from '../TaskAuthHelper';
import TaskTopTasksBar from '../TaskTopTasksBar';
import { getTaskViewModalStackStyles, getTaskViewModalStyles } from '../View/TaskRenderHelpers';
import { TaskDetailOwner } from './Details/TaskDetailOwner';
import { TaskDetailPanel } from './Details/TaskDetailPanel';
import { EventSubTasks } from './EventSubTasks';
import { EventTaskDetail } from './EventTaskDetail';
import { ApprovalTaskDetail } from './SystemTaskTypes/ApprovalTaskDetail';
import { TaskNewViewHeader, TaskNewViewLayout, TaskNewViewPanelTabs, useTaskDetailPanelState } from './TaskNewView';
import { getShownTaskPivotKeys, getTaskPivotTabs, isNewTaskDetailView } from './taskPivotTabs';
import TaskWorkflowDetailsModal from './TaskWorkflowDetailsModal';
import { useTaskPivotCounts } from './useTaskPivotCounts';
import { TaskAIPane } from './TaskAIPane';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';

interface IEventTaskProps extends ISingleTaskTypeProps {
  onSaveAsNewTask: () => void;
  onChangeCompletionDate: (completionDate: Date) => void;
}

const EventTask = (props: IEventTaskProps) => {
  const { t } = useTranslation(['task', 'control', 'translation', 'dateTimeComponent']);
  const appContext = useContext(AppContext);
  // With a side pane the two task columns share the room that is left (the same split as NormalTask).
  const hasSidePane = props.sidePane !== undefined;
  const detailsColumnWidth = hasSidePane ? '35%' : '50%';
  const pivotColumnWidth = hasSidePane ? '35%' : '50%';
  const [newEventHasWorkflows, setNewEventHasWorkflows] = useState<boolean>(false);
  const [err, setErr] = useState<Joi.ValidationError | undefined>(undefined);
  const [selectedPivot, setSelectedPivot] = useState<string>(props.selectedPivot ?? 'Checklist');
  const [showRemoveDialog, setShowRemoveDialog] = useState<boolean>(false);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const { pivotCounts, onActivityCountChange } = useTaskPivotCounts(props.task);
  const [showWorkflowDetails, setShowWorkflowDetails] = useState<boolean>(false);
  const [formOffersDocumentUpload, setFormOffersDocumentUpload] = useState<boolean | undefined>(undefined);
  const [webhooksLoadedForTemplateId, setWebhooksLoadedForTemplateId] = useState<number>(0);
  const [showNotAllSubtasksCompletedDialog, setShowNotAllSubtasksCompletedDialog] = useState<boolean>(false);

  //the redesigned dialog follows the same user setting as normal tasks; the mobile view and
  //approvals keep the classic layout
  const [taskDetailView, setTaskDetailView] = useState<TaskDetailViewMode>(
    () => toTaskDetailViewMode(appContext.globalDataCache.userSettings.get(TaskDetailView) as string | undefined),
  );
  const isNewView =
    isNewTaskDetailView(taskDetailView, appContext.isMobileView, props.task.taskId) &&
    props.task.systemTaskType !== SystemTaskTypes.Approval;
  const pivotTabs = getTaskPivotTabs(props.task, {
    isNewView,
    compactView: appContext.isMobileView,
    disallowSave: props.disallowSave,
    hasFormFeature: props.hasFormFeature,
    isEvent: true,
  });
  const panelState = useTaskDetailPanelState();
  const [panelPivot, setPanelPivot] = useState<string>('TaskDetails');
  const [showTaskId, setShowTaskId] = useState<boolean>(
    () => getLocalStorageData(appContext, LocalStorageKeys.TaskShowId) === '1',
  );

  useEffect(() => {
    //the flag belongs to the loaded form: hide the upload button again until the new form has loaded
    setFormOffersDocumentUpload(undefined);
  }, [props.task.taskId, props.task.taskTypeId]);

  useEffect(() => {
    const _localizedFields = {
      assignment: t('task:Assignment.Label'),
      startDateTime: t('task:Start.Label'),
      endDateTime: t('task:End.ErrorMessage'),
      name: t('task:Name.Label'),
      deadline: t('task:Deadline.ErrorMessage'),
    };

    const loadWebhooks = async () => {
      try {
        if (!props.task.templateId) return;
        //check if the template that created this event has connected webhooks (which trigger a workflow)
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const webHooks = await apiGetWebhooksForType('task', props.task.templateId, accessToken);
        setNewEventHasWorkflows(webHooks.length > 0);
      } catch (err) {
        props.onError(err);
      }
    };

    const result = props.task.validate(_localizedFields);
    setErr(result.error);

    if (props.task.taskId === -1 && props.task.templateId && props.task.templateId !== webhooksLoadedForTemplateId) {
      setWebhooksLoadedForTemplateId(props.task.templateId);
      loadWebhooks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task]);

  useEffect(() => {
    if (appContext.isMobileView) {
      setSelectedPivot('TaskDetails');
      props.setFormMode(false);
    } else if (props.task.taskTypeId && props.hasFormFeature) {
      setSelectedPivot('TaskType');
    } else {
      setSelectedPivot('Checklist');
    }
    // setFormMode is re-created by the parent on every render: listing it here would re-run this
    // effect continuously and keep resetting the pivot the user just picked
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appContext.isMobileView, props.hasFormFeature, props.task.taskTypeId]);

  useEffect(() => {
    //Single task requests to show a specific pivot
    if (props.selectedPivot) {
      setSelectedPivot(props.selectedPivot);
    }
  }, [props.selectedPivot]);

  //SingleTask opens a tab of its own accord (the library after an upload, the context for a follow-up):
  //in the new view that tab lives in the side panel, so show the panel when it was collapsed
  const prevRequestedPivot = useRef<string | undefined>(props.selectedPivot);
  useEffect(() => {
    const prev = prevRequestedPivot.current;
    prevRequestedPivot.current = props.selectedPivot;
    if (!isNewView || prev === undefined || !props.selectedPivot || props.selectedPivot === prev) return;
    if (props.selectedPivot === 'Checklist' || props.selectedPivot === 'TaskType') return;

    setPanelPivot(props.selectedPivot);
    panelState.onChangePanelOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedPivot]);

  useEffect(() => {
    props.onBeforeSaveCallback(onBeforeSave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //
  // Validate
  //
  const onBeforeSave = async (task: Task): Promise<Task | undefined> => {
    //when not all subtasks are completed, prevent completion of the event
    if (task.isCompleted() && task.isSeries() && task.instances) {
      for (let idx = 0; idx < task.instances.length; idx++) {
        if (!task.instances[idx].isCompleted()) {
          setShowNotAllSubtasksCompletedDialog(true);
          task.taskStateId = props.orgTask.taskStateId;

          return undefined;
        }
      }
    }

    return task;
  };

  const validate = (fieldName: string): string | undefined => {
    if (err) {
      const fieldError = err.details.find((e) => {
        return e.context?.key === fieldName || e.message.toLowerCase().includes(fieldName.toLowerCase());
      });

      return fieldError ? fieldError.message : undefined;
    }

    return undefined;
  };

  const hasErrors = (): boolean => {
    return err ? true : false;
  };

  const cannotSaveTask = (): boolean => {
    return (props.task.taskId !== -1 && props.task.isEqual(props.orgTask)) || hasErrors();
  };

  const getIsOutlookCreateDisabled = (): boolean => {
    if (props.task.isSeries()) {
      const instancesCanBeScheduled = props.task.instances?.filter(
        (i) =>
          props.task.isAssigned() &&
          !props.task.isScheduled() &&
          canUpdateTaskField(i, props.canUpdate, TaskFieldTypes.OutlookCreate, appContext),
      );

      return (
        hasErrors() ||
        props.isActionPending ||
        instancesCanBeScheduled?.length === 0 || //at least one instance must be assigned and not scheduled yet
        (props.disallowSchedule ?? false)
      );
    } else {
      return (
        hasErrors() ||
        props.isActionPending ||
        !props.task.isAssigned() ||
        (props.disallowSchedule ?? false) ||
        (props.disallowSave ?? false) || //do not allow to schedule subtasks when the parent event is open: parent will schedule in outlook
        !canUpdateTaskField(props.task, props.canUpdate, TaskFieldTypes.OutlookRemove, appContext)
      );
    }
  };

  const onChangeShowTaskId = (value: boolean) => {
    setShowTaskId(value);
    setLocalStorageData(appContext, LocalStorageKeys.TaskShowId, value ? '1' : '0', false);
  };

  const onCancel = () => {
    //queued activity messages of a new task are lost on cancel, so they count as unsaved changes
    if (cannotSaveTask() && !props.pendingComments?.length) {
      props.onCancel();
    } else {
      setShowCancelDialog(true);
    }
  };

  const onUpdateCheckList = (newCheckList: TaskCheckList) => {
    const copyTask = props.task.clone();
    copyTask.checkList = newCheckList;
    props.onUpdateTask(copyTask);
  };

  const onRenameTask = (newName: string) => {
    const copyTask = props.task.clone();
    copyTask.name = newName.replace(/\s*[\r\n]+\s*/g, ' ').trim();
    props.onUpdateTask(copyTask);
  };

  const onChangeClassicView = (classic: boolean) => {
    const newView: TaskDetailViewMode = classic ? 'classic' : 'new';
    setTaskDetailView(newView);
    void appContext.globalDataCache.userSettings.save(TaskDetailView, newView);
    //the two views have different tabs: pick the one that fits the layout
    setSelectedPivot(props.task.taskTypeId && props.hasFormFeature ? 'TaskType' : 'Checklist');
  };

  const getTaskDetails = () => {
    switch (props.task.systemTaskType) {
      case SystemTaskTypes.Approval:
        return (
          <ApprovalTaskDetail
            task={props.task}
            orgTask={props.orgTask}
            onUpdate={(newTask: Task) => {
              props.onUpdateTask(newTask);
            }}
            getErrorMessage={validate}
            isActionPending={props.isActionPending}
            tags={props.tags}
            addTagToTaskState={props.addTagToTaskState}
            removeTagFromTaskState={props.removeTagFromTaskState}
            onChangeCompletionDate={props.onChangeCompletionDate}
            windowLevel={props.windowLevel}
            canUpdate={props.canUpdate}
          />
        );
      default:
        return (
          <EventTaskDetail
            task={props.task}
            orgTask={props.orgTask}
            onUpdate={(newTask: Task) => {
              props.onUpdateTask(newTask);
            }}
            getErrorMessage={validate}
            isActionPending={props.isActionPending}
            tags={props.tags}
            addTagToTaskState={props.addTagToTaskState}
            removeTagFromTaskState={props.removeTagFromTaskState}
            onChangeCompletionDate={props.onChangeCompletionDate}
            windowLevel={props.windowLevel}
            navigateExternal={props.navigateExternal}
            canUpdate={props.canUpdate}
          />
        );
    }
  };

  const onClickFinish = () => {
    const newTask = props.task.clone();
    newTask.setComplete();
    props.onUpdateTask(newTask);
    props.onSave(newTask, false);
  };

  const onClickArchive = () => {
    const newTask = props.task.clone();
    newTask.setArchived();
    props.onUpdateTask(newTask);
    props.onSave(newTask, false);
  };

  //
  // Render helpers
  //
  const getWorkflowTaskMessage = (newEventHasWorkflows: boolean): JSX.Element | null => {
    if (appContext.isMobileView) return null;
    if (props.task.taskId === -1 && newEventHasWorkflows) {
      return <InfoMessage message={t('task:Workflows.Messages.Start')} />;
    } else {
      if (props.task.webhookStatus === TaskWorkflowStatus.Success) {
        return (
          <InfoMessage>
            <IPStack horizontal tokens={globalStackTokensGapExtraSmall}>
              <IPText variant="small"> {t('task:Workflows.Messages.Success')} </IPText>
              <IPLink onClick={() => setShowWorkflowDetails(true)}>{t('task:Workflows.Messages.DetailLink')}</IPLink>
            </IPStack>
          </InfoMessage>
        );
      } else if (props.task.webhookStatus === TaskWorkflowStatus.Failure) {
        return (
          <WarningMessage>
            <IPStack horizontal tokens={globalStackTokensGapExtraSmall}>
              <IPText styles={{ root: { color: 'black' } }} variant="small">
                {t('task:Workflows.Messages.Failure')}
              </IPText>
              <IPLink onClick={() => setShowWorkflowDetails(true)}>{t('task:Workflows.Messages.DetailLink')}</IPLink>
            </IPStack>
          </WarningMessage>
        );
      } else {
        return null;
      }
    }
  };

  const getOutlookButton = (): JSX.Element | null => {
    if (props.task.isSeries()) {
      //show create button when some subtasks are not scheduled yet
      if (props.task.instances?.some((i) => !i.eventId)) {
        //create for series
        //in this case, the back-end will schedule all subtasks in Outlook
        return (
          <IPStack>
            <IPStack.Item>
              <IPPrimaryButton
                className="isoplanner-task-scheduleinoutlook"
                disabled={getIsOutlookCreateDisabled()}
                onClick={() => {
                  props.createInOutlook();
                }}
              >
                {t('task:Outlook.CreateOutlook')}
              </IPPrimaryButton>
            </IPStack.Item>
          </IPStack>
        );
      } else if (props.task.instances && props.task.instances.length > 0) {
        //remove for series, show when all subtasks are scheduled
        //in this case, the back-end will remove all subtasks from Outlook
        return (
          <IPStack>
            <IPStack.Item>
              <IPPrimaryButton
                text={t('task:Outlook.RemoveOutlook')}
                disabled={
                  hasErrors() ||
                  props.isActionPending ||
                  !canUpdateTaskField(props.task, props.canUpdate, TaskFieldTypes.OutlookCreate, appContext)
                }
                onClick={() => {
                  props.removeFromOutlook();
                }}
              />
            </IPStack.Item>
          </IPStack>
        );
      }
    } else {
      if (!props.task.eventId && props.task.taskId !== -1) {
        //create for subtasks
        return (
          <IPStack>
            <IPStack.Item>
              <IPPrimaryButton
                className="isoplanner-task-scheduleinoutlook"
                disabled={getIsOutlookCreateDisabled()}
                onClick={() => {
                  props.createInOutlook();
                }}
              >
                {t('task:Outlook.CreateOutlook')}
              </IPPrimaryButton>
            </IPStack.Item>
          </IPStack>
        );
      } else if (props.task.eventId && props.task.taskId !== -1) {
        //remove
        return (
          <IPStack>
            <IPStack.Item>
              <IPPrimaryButton
                text={t('task:Outlook.RemoveOutlook')}
                disabled={
                  hasErrors() ||
                  props.isActionPending ||
                  !canUpdateTaskField(props.task, props.canUpdate, TaskFieldTypes.OutlookCreate, appContext)
                }
                onClick={() => {
                  props.removeFromOutlook();
                }}
              />
            </IPStack.Item>
          </IPStack>
        );
      }
    }

    return null;
  };

  const openEvent = async (taskMasterid: number | undefined) => {
    try {
      if (taskMasterid) {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const event = await apiGetSingleTask(taskMasterid, true, accessToken, appContext.globalDataCache);
        if (event && event.tasks.length === 1) {
          props.initData(event.tasks[0]);
        }
      }
    } catch (err) {
      appContext.setError(err);
    }
  };

  const onRenderTaskTypePivotItem = (
    itemProps?: IPivotItemProps,
    defaultRender?: (itemProps?: IPivotItemProps) => JSX.Element | null,
  ): JSX.Element | null => {
    //itemScope contains true when in compact view
    if (!itemProps || !itemProps.itemKey || !defaultRender) return null;

    //render default for compact mode
    if (itemProps.itemScope === true) {
      return defaultRender(itemProps);
    }

    return (
      <IPStack horizontal tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPText styles={itemProps.itemKey === selectedPivot ? globalTextStylesBold : undefined}>
            {itemProps.headerText}
          </IPText>
        </IPStack.Item>
        <IPStack.Item>
          <IPTooltipHost content={t('forms:View.EnterFormMode')}>
            <IPIconButton iconProps={{ iconName: 'Fullscreen' }} onClick={() => props.setFormMode(true)} />
          </IPTooltipHost>
        </IPStack.Item>
      </IPStack>
    );
  };

  //
  // Footer
  //
  const footer = (
    <IPStack horizontal={!appContext.isMobileView} verticalAlign="end" tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPStack horizontal tokens={globalStackTokensGapSmall}>
          <IPPrimaryButton
            disabled={cannotSaveTask() || props.isActionPending}
            onClick={async () => {
              props.onSave(props.task, false);
            }}
          >
            {t('translation:General.Button.Save')}
          </IPPrimaryButton>
          <IPDefaultButton
            onClick={() => {
              onCancel();
            }}
          >
            {t('translation:General.Button.Cancel')}
          </IPDefaultButton>
        </IPStack>
      </IPStack.Item>
      <IPStack.Item grow>
        <IPStack horizontal horizontalAlign={appContext.isMobileView ? undefined : 'space-around'}>
          {getOutlookButton()}
        </IPStack>
      </IPStack.Item>
      {!appContext.isMobileView && props.task.systemTaskType !== SystemTaskTypes.Approval && (
        <IPStack.Item>
          <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapMedium}>
            {isNewView && (
              <IPToggle
                inlineLabel
                label={t('task:Detail.ShowTaskId')}
                checked={showTaskId}
                onChange={(_ev, checked) => onChangeShowTaskId(!!checked)}
                styles={{ root: { marginBottom: 0 } }}
              />
            )}
            {props.task.taskId !== -1 && (
              <IPToggle
                inlineLabel
                label={t('task:Detail.ClassicView')}
                checked={taskDetailView === 'classic'}
                onChange={(_ev, checked) => onChangeClassicView(!!checked)}
                styles={{ root: { marginBottom: 0 } }}
              />
            )}
          </IPStack>
        </IPStack.Item>
      )}
    </IPStack>
  );

  const getTopTasksBar = (): JSX.Element => (
    <TaskTopTasksBar
      task={props.task}
      templateTypes={props.task.isSeries() ? undefined : [TaskTypes.Template]}
      onClickTemplate={props.copyFromTemplate}
      onClickFinish={onClickFinish}
      onClickArchive={onClickArchive}
      onClickFollowUp={props.onClickFollowUp}
      onClickRemove={() => setShowRemoveDialog(true)}
      onCreateNewTaskTemplate={props.onCreateNewTaskTemplate}
      onSaveAsNewTask={props.onSaveAsNewTask}
      onConvertTaskType={props.task.isSeries() ? props.onConvertTaskType : undefined}
      onClickOpenEvent={!props.windowLevel ? openEvent : undefined}
      navigateExternal={props.navigateExternal}
      canUpdate={props.canUpdate}
      canDelete={props.canDelete}
      orgTask={props.orgTask}
      canSave={!hasErrors()}
      isLoading={props.isLoading}
      isActionPending={props.isActionPending}
      onUploadEvidence={props.onUploadEvidence}
      formOffersDocumentUpload={formOffersDocumentUpload}
      assistantAction={props.assistantAction}
      onOpenAssistant={props.onOpenAssistant}
    />
  );

  const getTaskTypeForm = (isOpen: boolean): JSX.Element => (
    <TaskTypeForm
      isOpen={isOpen}
      task={props.task}
      links={props.links}
      compact={true}
      compactTaskView={props.compactView}
      //the saved state decides: picking another status must not unlock the form
      readonly={props.orgTask.isCompleted()}
      onUpdateData={props.onUpdateKPIData}
      onSetData={props.onSetKPIData}
      onUpdateTaskForForm={props.onUpdateTaskForForm}
      onFormLoaded={(elements) => setFormOffersDocumentUpload(elements.some((elm) => elm.offersDocumentUpload()))}
      addLinks={props.addLinkToTaskState}
      setLinks={props.setLinkToTaskState}
      removeLink={props.removeLinkFromTaskState}
      showErrors={props.showFormErrors}
      onValidate={props.onValidateForm}
      formMode={props.formMode}
      onExitFormMode={() => props.setFormMode(false)}
      onClickFinish={onClickFinish}
    ></TaskTypeForm>
  );

  const getPivotPanels = (pivot: string): JSX.Element => (
    <Fragment>
      {pivot === 'Resources' && (
        <TaskLinkList
          taskLinks={props.links}
          task={props.task}
          addLinks={props.addLinkToTaskState}
          setLinks={props.setLinkToTaskState}
          removeLink={props.removeLinkFromTaskState}
          navigateExternal={props.navigateExternal}
        />
      )}
      {pivot === 'Activities' && (
        <IPStack.Item grow>
          <ActivityTrail
            onCommentCountChange={onActivityCountChange}
            auditTrailId={props.task.auditTrailId}
            commentTrailId={props.task.commentTrailId}
            taskId={props.task.taskId}
            authSchemaIds={[props.task.authSchemaId]}
            onError={props.onError}
            pendingComments={props.pendingComments}
            onQueueComment={props.onQueueComment}
            onRemovePendingComment={props.onRemovePendingComment}
          />
        </IPStack.Item>
      )}
      {pivot === 'Audit' && (
        <IPStack.Item grow>
          <AuditTrail auditTrailId={props.task.auditTrailId} />
        </IPStack.Item>
      )}
      {pivot === 'Context' && (
        <IPStack.Item grow>
          <TaskContext
            orgTask={props.orgTask}
            task={props.task}
            updateTaskForForm={props.onUpdateTaskForForm}
            updateTask={props.onUpdateTask}
            onClickFollowUp={props.onClickFollowUp}
            onLinkFollowUpToTemplate={props.onLinkFollowUpToTemplate}
            onCreateEmptyFollowUpTask={props.onCreateEmptyFollowUpTask}
            allowEdit={canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.ChecklistEdit, appContext)}
            windowLevel={props.windowLevel}
            navigateExternal={true}
            narrow={isNewView}
          />
        </IPStack.Item>
      )}
    </Fragment>
  );

  const getClassicBody = (): JSX.Element => (
    <IPStack
      verticalFill
      tokens={globalStackTokensGapSmall}
      styles={getTaskViewModalStackStyles(appContext.isMobileView, false, hasSidePane)}
    >
      <IPStack.Item>
        <IPStack horizontal verticalAlign="center" horizontalAlign="space-between">
          <IPStack.Item>
            <IPStack horizontal={!appContext.isMobileView} verticalAlign="center" tokens={globalStackTokensGapMedium}>
              <IPStack.Item>
                <IPText variant="xxLarge">
                  {props.task.taskId === -1 && props.task.isSeries() && t('task:Title.Event.Series.New')}
                  {props.task.taskId === -1 && !props.task.isSeries() && t('task:Title.Event.New')}
                  {props.task.taskId !== -1 && props.task.isSeries() && t('task:Title.Event.Series.Edit')}
                  {props.task.taskId !== -1 && !props.task.isSeries() && t('task:Title.Event.Edit')}
                </IPText>
              </IPStack.Item>
            </IPStack>
          </IPStack.Item>
          {!appContext.isMobileView && <IPStack.Item>{getTopTasksBar()}</IPStack.Item>}
          <IPStack.Item>
            <IPIconButton iconProps={cancelIcon} onClick={onCancel} />
          </IPStack.Item>
        </IPStack>
        <IPStack.Item>
          <TaskDetailOwner
            orgTask={props.orgTask}
            task={props.task}
            onUpdate={props.onUpdateTask}
            canUpdate={props.canUpdate}
          />
        </IPStack.Item>
        <IPSeparator />
        <ErrorMessage error={props.error} customAction={props.onAfterErrorAction} />
        {getWorkflowTaskMessage(newEventHasWorkflows)}
      </IPStack.Item>
      {appContext.isMobileView && <IPStack.Item>{getTopTasksBar()}</IPStack.Item>}
      <IPStack.Item grow>
        <IPStack horizontal styles={globalStackStylesHeight100} tokens={globalStackTokensGapSmall}>
          {!appContext.isMobileView && (
            <Fragment>
              <IPStack.Item styles={{ root: { width: detailsColumnWidth, minWidth: 0 } }}>
                {getTaskDetails()}
              </IPStack.Item>
              <IPSeparator vertical></IPSeparator>
            </Fragment>
          )}
          <IPStack.Item
            styles={
              !appContext.isMobileView
                ? { root: { width: pivotColumnWidth, minWidth: 0 } }
                : { root: { width: '100%' } }
            }
          >
            <IPStack verticalFill tokens={globalStackTokensGapSmall}>
              <IPStack.Item>
                <IPPivot
                  selectedKey={selectedPivot}
                  onLinkClick={(item) => setSelectedPivot(item?.props.itemKey ?? '')}
                  overflowBehavior="menu"
                >
                  {pivotTabs.taskDetails && (
                    <IPPivotItem
                      className="isoplanner-task-pivot-details"
                      key="TaskDetails"
                      itemKey="TaskDetails"
                      headerText={t('task:Pivot.TaskDetails')}
                    />
                  )}
                  {pivotTabs.taskType && (
                    <IPPivotItem
                      className="isoplanner-task-pivot-tasktype"
                      key="TaskType"
                      itemKey="TaskType"
                      headerText={TaskType.tabNameForTask(props.task.taskTypeId, props.task.formVersion, appContext)}
                      onRenderItemLink={appContext.isMobileView ? undefined : onRenderTaskTypePivotItem}
                    />
                  )}
                  {pivotTabs.checklist && (
                    <IPPivotItem
                      className="isoplanner-task-pivot-checklist"
                      key="Checklist"
                      itemKey="Checklist"
                      headerText={t('task:Pivot.Checklist')}
                      itemCount={pivotCounts.checklist}
                    />
                  )}
                  <IPPivotItem
                    className="isoplanner-task-pivot-context"
                    key="Context"
                    itemKey="Context"
                    headerText={t('task:Pivot.Context')}
                    itemCount={pivotCounts.context}
                  />
                  <IPPivotItem
                    className="isoplanner-task-pivot-library"
                    key="Resources"
                    itemKey="Resources"
                    headerText={t('task:Pivot.Resources')}
                    itemCount={pivotCounts.resources}
                  />
                  {pivotTabs.activities && (
                    <IPPivotItem
                      className="isoplanner-task-pivot-activity"
                      key="Activities"
                      itemKey="Activities"
                      headerText={t('task:Pivot.Activities')}
                      itemCount={pivotCounts.activity}
                    />
                  )}
                  {pivotTabs.audit && (
                    <IPPivotItem
                      className="isoplanner-task-pivot-changelog"
                      key="Audit"
                      itemKey="Audit"
                      headerText={t('task:Pivot.Audit')}
                    />
                  )}
                </IPPivot>
              </IPStack.Item>
              {selectedPivot === 'TaskDetails' && (
                <Fragment>
                  <IPStack.Item grow style={{ height: appContext.isMobileView ? '40vh' : 0 }}>
                    {getTaskDetails()}
                  </IPStack.Item>
                </Fragment>
              )}
              {getTaskTypeForm(selectedPivot === 'TaskType')}
              {selectedPivot === 'Checklist' && (
                <ChecklistContainer
                  task={props.task}
                  orgTask={props.orgTask}
                  onUpdateCheckList={onUpdateCheckList}
                  mode={ChecklistMode.User}
                  canUpdate={props.canUpdate}
                />
              )}
              {getPivotPanels(selectedPivot)}
            </IPStack>
          </IPStack.Item>
          {/* the AI assistant: a third column beside the task, opened by the AI button */}
          {hasSidePane && <TaskAIPane>{props.sidePane}</TaskAIPane>}
        </IPStack>
      </IPStack.Item>
      <IPStack.Item>
        <IPSeparator styles={{ root: { height: 1 } }} />
      </IPStack.Item>
      <IPStack.Item>{footer}</IPStack.Item>
    </IPStack>
  );

  const hasForm = Boolean(props.hasFormFeature && props.task.taskTypeId && props.task.taskTypeId > 0);

  const getNewViewChecklist = (): JSX.Element => (
    <IPStack.Item grow styles={{ root: { minHeight: 0 } }}>
      <ChecklistContainer
        task={props.task}
        orgTask={props.orgTask}
        onUpdateCheckList={onUpdateCheckList}
        mode={ChecklistMode.User}
        canUpdate={props.canUpdate}
      />
    </IPStack.Item>
  );

  const getNewViewHeader = (): JSX.Element => (
    <TaskNewViewHeader
      title={props.task.name}
      taskId={props.task.taskId}
      showTaskId={showTaskId}
      canRename={canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.Name, appContext)}
      onRename={onRenameTask}
      panelState={panelState}
      onClose={onCancel}
      topBar={getTopTasksBar()}
      messages={
        <Fragment>
          <ErrorMessage error={props.error} customAction={props.onAfterErrorAction} />
          {getWorkflowTaskMessage(newEventHasWorkflows)}
        </Fragment>
      }
    />
  );

  //a series: its subtasks are the main column. An event without subtasks gets the form or the checklist there.
  const getNewViewMainColumn = (): JSX.Element => (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      {props.task.isSeries() && (
        <EventSubTasks
          task={props.task}
          isActionPending={props.isActionPending}
          windowLevel={props.windowLevel}
          navigateExternal={props.navigateExternal}
          hideIcon
          onUpdate={props.onUpdateTask}
        />
      )}
      {!props.task.isSeries() && (hasForm ? getTaskTypeForm(true) : getNewViewChecklist())}
    </IPStack>
  );

  const getNewViewRightColumn = (): JSX.Element => {
    //the form or the checklist is a side panel tab of a series only
    //a tab this event does not show falls back to the details
    const rightPivot = getShownTaskPivotKeys(pivotTabs).includes(panelPivot) ? panelPivot : 'TaskDetails';

    return (
      <IPStack verticalFill tokens={globalStackTokensGapSmall}>
        <TaskNewViewPanelTabs
          selectedKey={rightPivot}
          onSelect={setPanelPivot}
          onCollapse={() => panelState.onChangePanelOpen(false)}
        >
          <IPPivotItem
            className="isoplanner-task-pivot-details"
            key="TaskDetails"
            itemKey="TaskDetails"
            headerText={t('task:Pivot.TaskDetails')}
          />
          {pivotTabs.taskType && (
            <IPPivotItem
              className="isoplanner-task-pivot-tasktype"
              key="TaskType"
              itemKey="TaskType"
              headerText={TaskType.tabNameForTask(props.task.taskTypeId!, props.task.formVersion, appContext)}
            />
          )}
          {pivotTabs.checklist && (
            <IPPivotItem
              className="isoplanner-task-pivot-checklist"
              key="Checklist"
              itemKey="Checklist"
              headerText={t('task:Pivot.Checklist')}
              itemCount={pivotCounts.checklist}
            />
          )}
          <IPPivotItem
            className="isoplanner-task-pivot-context"
            key="Context"
            itemKey="Context"
            headerText={t('task:Pivot.Context')}
            itemCount={pivotCounts.context}
          />
          {/* Activity ranks before Library: the narrow pane moves the last tabs into the overflow menu */}
          {pivotTabs.activities && (
            <IPPivotItem
              className="isoplanner-task-pivot-activity"
              key="Activities"
              itemKey="Activities"
              headerText={t('task:Pivot.Activities')}
              itemCount={pivotCounts.activity}
            />
          )}
          <IPPivotItem
            className="isoplanner-task-pivot-library"
            key="Resources"
            itemKey="Resources"
            headerText={t('task:Pivot.Resources')}
            itemCount={pivotCounts.resources}
          />
          {pivotTabs.audit && (
            <IPPivotItem
              className="isoplanner-task-pivot-changelog"
              key="Audit"
              itemKey="Audit"
              headerText={t('task:Pivot.Audit')}
            />
          )}
        </TaskNewViewPanelTabs>
        {rightPivot === 'TaskDetails' && (
          <IPStack.Item grow>
            <TaskDetailPanel
              task={props.task}
              orgTask={props.orgTask}
              onUpdate={props.onUpdateTask}
              getErrorMessage={validate}
              isActionPending={props.isActionPending}
              loadRescheduleRange={props.loadRescheduleRange}
              tags={props.tags}
              addTagToTaskState={props.addTagToTaskState}
              removeTagFromTaskState={props.removeTagFromTaskState}
              onChangeCompletionDate={props.onChangeCompletionDate}
              canUpdate={props.canUpdate}
              isEventSeries={props.task.isSeries()}
            />
          </IPStack.Item>
        )}
        {props.task.isSeries() && hasForm && getTaskTypeForm(rightPivot === 'TaskType')}
        {rightPivot === 'Checklist' && getNewViewChecklist()}
        {getPivotPanels(rightPivot)}
      </IPStack>
    );
  };

  const getNewViewBody = (): JSX.Element => (
    <TaskNewViewLayout
      panelState={panelState}
      header={getNewViewHeader()}
      main={getNewViewMainColumn()}
      panel={getNewViewRightColumn()}
      footer={footer}
      sidePane={props.sidePane}
    />
  );

  //
  // Main render
  //
  return (
    <IPModal
      forceFocusInsideTrap={false}
      //the new view has the focusable title first: opening must not put the focus ring on it
      focusTrapZoneProps={isNewView ? { disableFirstFocus: true } : undefined}
      isOpen={props.isOpen}
      styles={getTaskViewModalStyles(props.windowLevel, isNewView && panelState.fullWindow)}
    >
      {props.isLoading && <OverlayLoader text={t('task:Progress.Save')} />}
      {isNewView ? getNewViewBody() : getClassicBody()}
      <DialogConfirmDelete
        onNo={() => {
          setShowRemoveDialog(false);
        }}
        onYes={() => {
          setShowRemoveDialog(false);
          props.onRemove();
        }}
        title={props.task.isSeries() ? t('task:Template.RemoveDialogTitle') : t('task:Remove.DialogTitle')}
        subText={props.task.isSeries() ? t('task:Template.RemoveDialogSubText') : t('task:Remove.DialogSubTitle')}
        hidden={!showRemoveDialog}
        confirmText={!props.task.isSeries() ? t('task:Remove.RemoveDialogConfirmText') : undefined}
      />
      <DialogYesNo
        onNo={() => {
          setShowCancelDialog(false);
        }}
        onYes={() => {
          setShowCancelDialog(false);

          props.onCancel();
        }}
        title={t('task:Cancel.DialogTitle')}
        subText={t('task:Cancel.DialogSubTitle')}
        hidden={!showCancelDialog}
      />
      <DialogOk
        onOk={() => {
          setShowNotAllSubtasksCompletedDialog(false);
        }}
        title={t('task:NotAllSubtasksCompletedDialog.Title')}
        subText={t('task:NotAllSubtasksCompletedDialog.SubText')}
        hidden={!showNotAllSubtasksCompletedDialog}
      />
      <TaskWorkflowDetailsModal
        hidden={!showWorkflowDetails}
        task={props.task}
        onClose={() => setShowWorkflowDetails(false)}
      />
    </IPModal>
  );
};

export default EventTask;
